import { useState } from "react";
import { Archive, ArchiveRestore, X } from "lucide-react";
import { EditorModal } from "@/components/EditorModal";
import { api, formatApiError } from "@/lib/api";

function formatStamp(value) {
  if (!value) return "";
  return new Date(value).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export const ClientArchiveConfirm = ({ client, onClose, onArchived }) => {
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  async function submit(event) {
    event.preventDefault();
    setSaving(true);
    try {
      await api.post(`/clients/${client.id}/archive`, { reason });
      onArchived(client);
    } catch (requestError) {
      setError(formatApiError(requestError));
      setSaving(false);
    }
  }

  return (
    <EditorModal onClose={onClose} testId="client-archive-modal">
      <section className="inline-edit-panel" data-testid="client-archive-confirm">
        <div className="section-heading">
          <div>
            <h2>Archive {client.name}?</h2>
            <p>The client leaves the active list but nothing is deleted. Its requests, assignments, budgets and documents are kept, and you can restore it from Archived clients at any time. This action is logged.</p>
          </div>
          <button className="projection-icon-button" data-testid="close-client-archive" type="button" onClick={onClose}><X size={16} /></button>
        </div>
        <form className="inline-edit-form" onSubmit={submit}>
          <label className="inline-edit-full">
            Reason (optional)
            <input data-testid="client-archive-reason-input" maxLength={300} placeholder="e.g. Contract ended, duplicate record" type="text" value={reason} onChange={(event) => setReason(event.target.value)} />
          </label>
          {error && <p className="form-message error-message" data-testid="client-archive-error">{error}</p>}
          <div className="board-confirm-actions">
            <button className="button button-secondary" data-testid="client-archive-cancel" type="button" onClick={onClose}>Cancel</button>
            <button className="button button-primary" data-testid="client-archive-submit" disabled={saving} type="submit"><Archive size={14} /> {saving ? "Archiving…" : "Archive client"}</button>
          </div>
        </form>
      </section>
    </EditorModal>
  );
};

export const ArchivedClientsSection = ({ clients, onRestore }) => {
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState("");
  const [error, setError] = useState("");
  if (!clients.length) return null;

  async function restore(client) {
    setBusyId(client.id);
    try {
      await api.post(`/clients/${client.id}/restore`);
      await onRestore(client);
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setBusyId("");
    }
  }

  return (
    <section className="archived-clients" data-testid="archived-clients-section">
      <button aria-expanded={open} className="archived-clients-toggle" data-testid="archived-clients-toggle" type="button" onClick={() => setOpen((value) => !value)}>
        <Archive size={15} /> Archived clients <strong data-testid="archived-clients-count">{clients.length}</strong>
      </button>
      {error && <p className="form-message error-message" data-testid="archived-clients-error">{error}</p>}
      {open && (
        <ul className="archived-clients-list" data-testid="archived-clients-list">
          {clients.map((client) => (
            <li key={client.id} data-testid={`archived-client-${client.id}`}>
              <div>
                <strong>{client.name}</strong>
                <span>Archived {formatStamp(client.archived_at)}{client.archived_by_name ? ` by ${client.archived_by_name}` : ""}{client.archive_reason ? ` · ${client.archive_reason}` : ""}</span>
              </div>
              <button className="button button-secondary" data-testid={`restore-client-${client.id}`} disabled={busyId === client.id} type="button" onClick={() => restore(client)}>
                <ArchiveRestore size={14} /> {busyId === client.id ? "Restoring…" : "Restore"}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
